import { useEffect } from 'react';
import { FormikProps, FormikProvider } from 'formik';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import PartiesCard from '../cards/parties-card/PartiesCard';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes, ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import PlacesCard from '../cards/places-card/PlacesCard';
import CountriesOfRoutingField from './cards/CountriesOfRoutingField';
import {
    AdditionalInformationCard,
    AdditionalSupplyChainActorCard,
    PassiveBorderTransportMeansCard,
    SupportingDocumentsCard,
    TransportEquipmentCard,
} from './cards/SharedCards';
import useICS2Codelists from 'hooks/useICS2Codelists';
import useHideEntities from 'hooks/useHideEntities';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';

type Props = {
    formik: FormikProps<any>;
    viewOnly: boolean | undefined;
};

const ROUTING_PATH = 'countriesOfRoutingOfConsignment';

const f11Parties = [
    {
        tabName: 'Consignor',
        label: 'Consignor',
        required: true,
        referenceNumber: '13 02 000 000',
        path: 'consignor',
        partyReferences: {
            name: '13 02 016 000',
            identificationNumber: '13 02 017 000',
            typeOfPerson: '13 02 019 000',
            streetAndNumber: '13 02 018 023',
            postCode: '13 02 018 021',
            city: '13 02 018 022',
            country: '13 02 018 020',
        },
    },
    {
        tabName: 'Consignee',
        label: 'Consignee',
        required: false,
        referenceNumber: '13 03 000 000',
        path: 'consignee',
        partyReferences: {
            name: '13 03 016 000',
            identificationNumber: '13 03 017 000',
            typeOfPerson: '13 03 019 000',
            streetAndNumber: '13 03 018 019',
            postCode: '13 03 018 021',
            city: '13 03 018 022',
            country: '13 03 018 020',
        },
    },
    {
        tabName: 'Notify Party',
        label: 'Notify Party',
        required: false,
        singleCommunication: true,
        referenceNumber: '13 13 000 000',
        path: 'notifyParty',
        partyReferences: {
            name: '13 13 016 000',
            identificationNumber: '13 13 017 000',
            typeOfPerson: '13 13 019 000',
            streetAndNumber: '13 13 018 023',
            postCode: '13 13 018 021',
            city: '13 13 018 022',
            country: '13 13 018 020',
        },
    },
    {
        tabName: 'Seller',
        label: 'Seller',
        required: false,
        referenceNumber: '13 08 000 000',
        path: 'goodsShipment.seller',
        partyReferences: {
            name: '13 08 016 000',
            identificationNumber: '13 08 017 000',
            typeOfPerson: '13 08 019 000',
            streetAndNumber: '13 08 018 019',
            postCode: '13 08 018 021',
            city: '13 08 018 022',
            country: '13 08 018 020',
        },
    },
    {
        tabName: 'Buyer',
        label: 'Buyer',
        required: false,
        referenceNumber: '13 09 000 000',
        path: 'goodsShipment.buyer',
        partyReferences: {
            name: '13 09 016 000',
            identificationNumber: '13 09 017 000',
            typeOfPerson: '13 09 019 000',
            streetAndNumber: '13 09 018 019',
            postCode: '13 09 018 021',
            city: '13 09 018 022',
            country: '13 09 018 020',
        },
    },
];

const f50Parties = [
    {
        tabName: 'Seller',
        label: 'Seller',
        required: false,
        referenceNumber: '13 08 000 000',
        path: 'goodsShipment.seller',
        partyReferences: {
            name: '13 08 016 000',
            identificationNumber: '13 08 017 000',
            typeOfPerson: '13 08 019 000',
            streetAndNumber: '13 08 018 019',
            postCode: '13 08 018 021',
            city: '13 08 018 022',
            country: '13 08 018 020',
        },
    },
    {
        tabName: 'Buyer',
        label: 'Buyer',
        required: false,
        referenceNumber: '13 09 000 000',
        path: 'goodsShipment.buyer',
        partyReferences: {
            name: '13 09 016 000',
            identificationNumber: '13 09 017 000',
            typeOfPerson: '13 09 019 000',
            streetAndNumber: '13 09 018 019',
            postCode: '13 09 018 021',
            city: '13 09 018 022',
            country: '13 09 018 020',
        },
    },
];

const ICS2ENSHouseConsignmentForm = ({ formik, viewOnly }: Props) => {
    const { preloadICS2Codelists } = useICS2Codelists();
    const { isFormType } = useHideEntities();

    const isF50 = isFormType([ICS2MessageTypes.F50]);

    useEffect(() => {
        preloadICS2Codelists([
            ICS2CodelistsMap.CL013,
            ICS2CodelistsMap.CL709,
            ICS2CodelistsMap.CL710,
            ICS2CodelistsMap.CL711,
            ICS2CodelistsMap.CL718,
            ICS2CodelistsMap.CL750,
            ICS2CodelistsMap.CL751,
            ICS2CodelistsMap.CL704,
            ICS2CodelistsMap.CL707,
        ]);
    }, [preloadICS2Codelists]);

    return (
        <FormikProvider value={formik}>
            <NewFormCard title="Details">
                <FormCardContainer>
                    <DeclarationInput
                        condensed
                        refNumber="12 08 001 000"
                        label="Reference Number/UCR"
                        {...getFormikProps('referenceNumberUCR.referenceNumberUCR', formik)}
                    />
                    <DeclarationNumberInput
                        condensed
                        required
                        refNumber="18 03 001 000"
                        label="Total Gross Mass"
                        maxLength={16}
                        precision={6}
                        {...getFormikProps('totalGrossMass', formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <PartiesCard viewOnly={viewOnly} formik={formik} parties={isF50 ? f50Parties : f11Parties} />

            <NewFormCard title="Transport Details">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Transport Method of Payment"
                        required
                        refNumber="14 02 038 000"
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL116,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps('transportCharges.methodOfPayment', formik)}
                    />
                    <CountriesOfRoutingField
                        formik={formik}
                        path={ROUTING_PATH}
                        label="Countries of Routing of Consignment"
                        refNumber="16 12 020 000"
                        codelistName={ICS2CodelistsMap.CL718}
                    />
                </FormCardContainer>
            </NewFormCard>

            <PassiveBorderTransportMeansCard formik={formik} viewOnly={viewOnly} nationalityRequired={isF50} />

            <PlacesCard
                sections={['placeOfAcceptance', 'placeOfDelivery']}
                formik={formik}
                getFieldHelpers={formik.getFieldHelpers}
                getFieldProps={formik.getFieldProps}
            />

            <NewFormCard title="Transport Document">
                <FormCardContainer>
                    <DeclarationInput
                        required
                        condensed
                        refNumber="12 05 001 000"
                        label="Document Number"
                        {...getFormikProps('transportDocumentHouseLevel.documentNumber', formik)}
                    />
                    <DeclarationSelect
                        required
                        condensed
                        hideCodelistMenu
                        label="Type"
                        refNumber="12 05 002 000"
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL754,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps('transportDocumentHouseLevel.type', formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <SupportingDocumentsCard formik={formik} viewOnly={viewOnly} />

            <NewFormCard title="Container">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Container Indicator"
                        refNumber="19 01 001 000"
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL708,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        required
                        condensed
                        {...getFormikProps('containerIndicator', formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <TransportEquipmentCard formik={formik} />

            <AdditionalSupplyChainActorCard formik={formik} viewOnly={viewOnly} />

            <AdditionalInformationCard formik={formik} viewOnly={viewOnly} />
        </FormikProvider>
    );
};

export default ICS2ENSHouseConsignmentForm;
